/**
 * What: The one bordered box on the Study Builder page: an optional header row (icon, title, trailing slot),
 *   an optional description, then content. Pass `onSelect` to make the whole box a button; `selected` draws the
 *   success-green border (the same green as the check icon) with no layout change.
 * Transport / data-flow: Pure presentation over caller-supplied content; the native s-box stays underneath
 *   so it still reads as Polaris. BuilderBox.css only adds the selected outline and hover state.
 */
import type { ReactNode } from 'react'
import './BuilderBox.css'

/** One grid for every row of BuilderBoxes, so the treatment tiles and the audience tiles read as the same thing. */
export const BUILDER_BOX_GRID_COLUMNS = 'repeat(auto-fit, minmax(180px, 1fr))'

type TestAttributes = Record<`data-${string}`, string | boolean>

export function BuilderBox({
  icon,
  title,
  description,
  trailing,
  children,
  selected = false,
  disabled = false,
  onSelect,
  attributes,
}: {
  /** An s-icon type, shown inline before the title. */
  icon?: string
  /** A string renders as bold text; pass an element to control the text yourself. */
  title?: ReactNode
  description?: ReactNode
  /** Right end of the header row: a check icon, badge or action. */
  trailing?: ReactNode
  children?: ReactNode
  selected?: boolean
  /** Only meaningful with `onSelect`: locks the box, dims it and removes the hover state. */
  disabled?: boolean
  /** Makes the whole box clickable. */
  onSelect?: () => void
  /** Extra data-* attributes for the outermost element. */
  attributes?: TestAttributes
}) {
  const selectable = Boolean(onSelect)
  const titleNode = typeof title === 'string' ? <s-text type="strong">{title}</s-text> : title
  const iconNode = icon ? <s-icon type={icon as never} /> : null
  const hasHeader = Boolean(icon || title || trailing)
  const header = hasHeader ? (
    <div className="tf-builder-box__header">
      <div className="tf-builder-box__title">
        {iconNode}
        {titleNode}
      </div>
      {trailing ? <div className="tf-builder-box__trailing">{trailing}</div> : null}
    </div>
  ) : null
  const descriptionNode = typeof description === 'string' ? <s-text color="subdued">{description}</s-text> : description
  const box = (
    <div
      className="tf-builder-box"
      data-selectable={selectable}
      data-selected={selected}
      data-disabled={disabled}
      {...(selectable ? undefined : attributes)}
    >
      <s-box border="base" borderRadius="base" padding="base" background="base" blockSize="100%">
        <s-stack direction="block" gap="small-200">
          {header}
          {descriptionNode}
          {children}
        </s-stack>
      </s-box>
    </div>
  )
  if (!onSelect) return box
  return (
    // blockSize="100%": inside a grid the box has to fill its row's height, or shorter content leaves short boxes.
    <s-clickable {...attributes} data-selected={selected} disabled={disabled} onClick={onSelect} blockSize="100%">
      {box}
    </s-clickable>
  )
}
